import { useState, useEffect, useCallback } from 'react';
import {
  configApiRef,
  useApi,
  gitlabAuthApiRef,
  microsoftAuthApiRef,
  type SignInPageProps,
} from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { UserIdentity } from '@backstage/core-components';
import { Flex, Text, Button } from '@backstage/ui';
import {
  RiMicrosoftFill,
  RiGitlabFill,
  RiUser3Fill,
} from '@remixicon/react';
import { appTranslationRef } from '../../core/i18n';
import logo from '../../shared/assets/logo/KPC23-Logotype-baseline-sans-fond-01.webp';
import styles from './sign-in.module.css';

type Provider = 'microsoft' | 'gitlab' | 'guest';

export const SESSION_KEY = 'kpc-sign-in-provider';

export const SignInPage = (props: SignInPageProps) => {
  const config = useApi(configApiRef);
  const { t } = useTranslationRef(appTranslationRef);
  const microsoftAuthApi = useApi(microsoftAuthApiRef);
  const gitlabAuthApi = useApi(gitlabAuthApiRef);
  const allowGuest = config.getOptionalBoolean('app.guestAuth');

  const [loading, setLoading] = useState<Provider | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(
    () => !!localStorage.getItem(SESSION_KEY),
  );

  const resolveIdentity = useCallback(
    async (
      provider: Provider,
      options: { silent?: boolean } = {},
    ) => {
      if (provider === 'guest') {
        return UserIdentity.createGuest();
      }

      const authApi =
        provider === 'microsoft' ? microsoftAuthApi : gitlabAuthApi;

      const response = await authApi.getBackstageIdentity({
        instantPopup: !options.silent,
        optional: options.silent,
      });
      if (!response) {
        return undefined;
      }
      const profile = await authApi.getProfile();
      return UserIdentity.create({
        identity: response.identity,
        authApi,
        profile,
      });
    },
    [microsoftAuthApi, gitlabAuthApi],
  );

  const signIn = useCallback(
    async (provider: Provider) => {
      setLoading(provider);
      setError(null);

      try {
        const identityApi = await resolveIdentity(provider);
        if (!identityApi) {
          throw new Error('No identity');
        }
        localStorage.setItem(SESSION_KEY, provider);
        props.onSignInSuccess(identityApi);
      } catch (err) {
        setLoading(null);
        const isUserRejection =
          err instanceof Error && err.message.includes('rejected by user');
        if (!isUserRejection) {
          setError(t('auth.loginFailed'));
        }
      }
    },
    [resolveIdentity, props, t],
  );

  // Session restore — silent (no popup)
  useEffect(() => {
    let cancelled = false;
    const saved = localStorage.getItem(SESSION_KEY) as Provider | null;

    if (saved) {
      resolveIdentity(saved, { silent: true }).then(
        identityApi => {
          if (cancelled) return;
          if (identityApi) {
            props.onSignInSuccess(identityApi);
          } else {
            localStorage.removeItem(SESSION_KEY);
            setRestoring(false);
          }
        },
        () => {
          if (cancelled) return;
          localStorage.removeItem(SESSION_KEY);
          setRestoring(false);
        },
      );
    } else {
      setRestoring(false);
    }

    return () => {
      cancelled = true;
    };
  }, [resolveIdentity, props]);

  if (restoring) {
    return <div className={styles.page} />;
  }

  return (
    <Flex
      align="center"
      justify="center"
      className={styles.page}
    >
      <Flex
        direction="column"
        align="center"
        gap="6"
        className={styles.card}
      >
        <img src={logo} height={64} alt="KPC" />

        <Flex direction="column" align="center" gap="1.5">
          <Text as="h1" variant="title-medium" weight="bold">
            {t('auth.welcome')}
          </Text>
          <Text variant="body-medium" color="secondary">
            {t('auth.subtitle')}
          </Text>
        </Flex>

        {error && (
          <div className={styles.error}>
            <Text variant="body-small">{error}</Text>
          </div>
        )}

        <Flex direction="column" gap="3" className={styles.providers}>
          <Button
            variant="secondary"
            iconStart={<RiMicrosoftFill size={18} />}
            loading={loading === 'microsoft'}
            isDisabled={loading !== null}
            onPress={() => signIn('microsoft')}
            className={styles.providerButton}
          >
            {t('auth.signInWithMicrosoft')}
          </Button>

          <Button
            variant="secondary"
            iconStart={<RiGitlabFill size={18} />}
            loading={loading === 'gitlab'}
            isDisabled={loading !== null}
            onPress={() => signIn('gitlab')}
            className={styles.providerButton}
          >
            {t('auth.signInWithGitlab')}
          </Button>

          {allowGuest && (
            <>
              <Flex align="center" gap="3" className={styles.divider}>
                <span className={styles.dividerLine} />
                <Text variant="body-x-small" color="secondary">{t('auth.or')}</Text>
                <span className={styles.dividerLine} />
              </Flex>

              <Button
                variant="tertiary"
                iconStart={<RiUser3Fill size={18} />}
                loading={loading === 'guest'}
                isDisabled={loading !== null}
                onPress={() => signIn('guest')}
                className={styles.providerButton}
              >
                {t('auth.continueAsGuest')}
              </Button>
            </>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};
